import styled, { css } from 'styled-components'
import { Button } from '../Button/Button'
import { getImageUrl, ImageQuality } from '@/utils'
import { useState } from 'react'
import { Spinner } from '../Spinner'

const ImgContainer = styled.div`
  position: relative;
  button {
    position: absolute;
    top: 8px;
    right: 16px;
  }
`
const StyledButton = styled(Button)`
  border-radius: 18px;
`

const StyledImage = styled.img<{ $shadow: boolean; $loading: boolean }>`
  display: ${({ $loading }) => ($loading ? 'none' : 'block')};
  width: 100%;
  border-radius: 12px;
  ${({ $shadow }) =>
    $shadow &&
    css`
      box-shadow: 1px 1px 8px ${({ theme }) => theme.colors.surface.border};
    `}
`

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  padding: 12px;
`

const StyledSpinner = styled(Spinner)`
  max-width: 100px;
  max-height: 100px;
  color: ${({ theme }) => theme.colors.brand.primary.active};
`

interface Props {
  className?: string
  imgSrc: string
  alt?: string
  btnAction?: () => void
  btnLabel?: string
  shadow?: boolean
  quality?: ImageQuality
  retryOptions?: {
    retryDelay?: number
    maxAttempts?: number
  }
}

/**
 * This is a special image for rendering the image with a certain quality.
 * By default it will render a 'web-friendly' image unless otherwise specified.
 *
 * @param props
 * @returns a styled image of a certain quality
 */
export const Image: React.FC<Props> = ({
  imgSrc,
  alt,
  btnLabel,
  btnAction,
  className,
  shadow = false,
  retryOptions = { maxAttempts: 0, retryDelay: 3000 },
}) => {
  const [attempt, setAttempt] = useState<number>(0)
  const [loading, setLoading] = useState<boolean>(true)

  const image = `${imgSrc}${imgSrc.includes('?') ? '&' : '?'}retry=${attempt}`

  const handleLoad = () => {
    setLoading(false)
  }

  const handleError = () => {
    if (attempt < (retryOptions?.maxAttempts ?? 0)) {
      setTimeout(() => {
        setAttempt((prev) => prev + 1)
        setLoading(true)
      }, retryOptions.retryDelay ?? 3000)
    } else {
      // If max retries reached, show an error state or fallback
      // setFailed(true)
      setLoading(false)
    }
  }

  return (
    <ImgContainer className={className}>
      {btnLabel && btnAction ? (
        <StyledButton kind="secondary" type="button" onClick={btnAction}>
          {btnLabel}
        </StyledButton>
      ) : null}
      <StyledImage
        src={getImageUrl(image, 'web-friendly')}
        alt={alt}
        onError={handleError}
        onLoad={handleLoad}
        $shadow={shadow}
        $loading={loading}
      />
      {loading && (
        <LoadingContainer>
          <StyledSpinner />
        </LoadingContainer>
      )}
    </ImgContainer>
  )
}
